
import utils from '@/utils';
import { defineComponent } from 'vue';
import { IHost } from '@/types/models';
import agentPing from '@/api/agent/ping';
import agentInstall from '@/api/agent/install';
import api, { IAddRequest, IEditRequest, IGetRequest, IShellResult } from '@/api/admin/hosts';
import { IElForm } from '@/types/element';
import { HostType } from '@/types/enums';
import Certs from './components/hosts/Certs.vue';
import Domains from './components/hosts/Domains.vue';
import Backups from './components/hosts/Backups.vue';

interface ICredentialsForm {
  hostId: number;
  securityKey: string;
  ftpPassword: string;
  databaseUser: string;
  databasePassword: string;
  databaseName: string;
}

interface IConnection {
  name: string;
  description: string;
  docUrl: string;
}

interface IResetPasswordForm {
  securityKey: string;
  guid: string;
  userName: string;
  password: string;
  confirmPassword: string;
}

interface IState {
  hosts: IHost[];
  nodeNames: string[];
  host: IHost;
  searchForm: IGetRequest;
  isAdd: boolean;
  addForm: IAddRequest;
  isEdit: boolean;
  editForm: IEditRequest;
  credentialsForm: ICredentialsForm;
  isCredentials: boolean;
  shellResult: IShellResult;
  isShell: boolean;
  isConnections: boolean;
  connections: IConnection[];
  isCerts: boolean;
  isDomains: boolean;
  isBackups: boolean;
  isResetPassword: boolean;
  resetPasswordForm: IResetPasswordForm;
}

export default defineComponent({
  components: {
    Certs,
    Domains,
    Backups,
  },
  data() {
    return {
      hosts: [],
      nodeNames: [],
      host: {} as IHost,
      searchForm: {
        nodeName: '',
        dateFrom: '',
        dateTo: '',
        keyword: '',
        orderByIdDesc: false,
      } as IGetRequest,
      isAdd: false,
      addForm: {} as IAddRequest,
      isEdit: false,
      editForm: {} as IEditRequest,
      credentialsForm: {} as ICredentialsForm,
      isCredentials: false,
      shellResult: {
        docker: '',
      } as IShellResult,
      isShell: false,
      isConnections: false,
      connections: [],
      isCerts: false,
      isDomains: false,
      isBackups: false,
      isResetPassword: false,
      resetPasswordForm: {} as IResetPasswordForm,
    } as IState;
  },
  methods: {
    apiGet() {
      api.get(this.searchForm, (res) => {
        this.hosts = res.hosts;
        this.nodeNames = res.nodeNames;

        for (const host of this.hosts) {
          host.isPingPong = true;
          if (host.isRunning) {
            host.isPingPong = false;
            this.apiPing(host);
          }
        }
      });
    },

    apiPing(host: IHost) {
      agentPing.ping(host.hostName, host.nodeName, host.isHongKong, (pong: string) => {
        if (pong === 'pong') {
          host.isPingPong = true;
        } else {
          host.isPingPong = false;
        }
      });
    },

    getUniqueName(host: IHost) {
      return utils.getHostUniqueName(host);
    },

    getHostType(host: IHost) {
      return utils.getHostType(host.hostType);
    },

    getUrl(hostName: string, nodeName: string, isHongKong: boolean): string {
      return utils.getHostUrl(hostName, nodeName, isHongKong);
    },

    getSize(mbSize: number) {
      return utils.getSize(mbSize);
    },

    isExpiration(host: IHost): boolean {
      const date = new Date();
      date.setDate(date.getDate() + 15);
      const expirationDate = new Date(host.expirationDate);
      return expirationDate.getTime() <= date.getTime();
    },

    btnHostClick(host: IHost) {
      this.host = host;
    },

    btnDeleteClick(host: IHost) {
      utils.alertDelete(
        {
          title: '删除网站云',
          text: `此操作将删除网站云 ${this.getUniqueName(host)}，确定吗？`,
          button: '确认删除',
        },
        () => {
          api.delete(host.id, (res) => {
            if (!res) return;
            this.apiGet();
            utils.success('网站云删除成功');
          });
        },
      );
    },

    btnSearchClick() {
      this.apiGet();
    },

    btnResetPasswordClick(host: IHost) {
      this.host = { ...host };
      api.resetPasswordGet(host.id, (res) => {
        this.resetPasswordForm.securityKey = res.securityKey;
        this.resetPasswordForm.guid = res.guid;
        this.resetPasswordForm.userName = '';
        this.resetPasswordForm.password = '';
        this.resetPasswordForm.confirmPassword = '';
        this.isResetPassword = true;
      });
    },

    btnMoreClick(args: { name: string; host: IHost }) {
      this.host = { ...args.host };
      if (args.name === 'Domains') {
        this.isDomains = true;
      } else if (args.name === 'ResetPassword') {
        this.btnResetPasswordClick(args.host);
      } else if (args.name === 'Backups') {
        this.isBackups = true;
      } else if (args.name === 'Start') {
        utils.alertDelete(
          {
            title: '启动实例',
            text: `此操作将启动实例 ${this.getUniqueName(this.host)}，确定吗？`,
            button: '确认启动',
          },
          () => {
            api.start({ hostId: args.host.id }, () => {
              utils.success('实例启动成功！');
              this.apiGet();
            });
          },
        );
      } else if (args.name === 'Stop') {
        utils.alertDelete(
          {
            title: '停止实例',
            text: `此操作将停止实例 ${this.getUniqueName(this.host)}，确定吗？`,
            button: '确认停止',
          },
          () => {
            api.stop({ hostId: args.host.id }, () => {
              utils.success('实例停止成功！');
              this.apiGet();
            });
          },
        );
      } else if (args.name === 'Reload') {
        utils.alertDelete(
          {
            title: '重启实例',
            text: `此操作将重启实例 ${this.getUniqueName(this.host)}，确定吗？`,
            button: '确认重启',
          },
          () => {
            api.reload({ hostId: args.host.id }, () => {
              utils.success('实例重启成功！');
              this.apiGet();
            });
          },
        );
      }
    },

    btnAddClick() {
      const d = new Date();
      const year = d.getFullYear();
      const month = d.getMonth();
      const day = d.getDate();
      const expirationDate = new Date(year + 1, month, day);
      this.addForm = {
        nodeName: '',
        userName: '',
        hostType: HostType.Starter,
        expirationDate,
      } as IAddRequest;
      this.isAdd = true;
    },

    btnAddSubmitClick() {
      (this.$refs.addForm as IElForm).validate((valid) => {
        if (valid) {
          api.add(this.addForm, (res) => {
            if (!res) return;
            this.isAdd = false;
            this.apiGet();
            utils.success('网站云添加成功');
          });
        }
      });
    },

    btnCertsClick(host: IHost) {
      this.host = { ...host };
      this.isCerts = true;
    },

    btnEditClick(host: IHost) {
      this.host = { ...host };
      this.editForm = {
        hostId: this.host.id,
        expirationDate: this.host.expirationDate,
      } as IEditRequest;
      this.isEdit = true;
    },

    btnEditSubmitClick() {
      (this.$refs.editForm as IElForm).validate((valid) => {
        if (valid) {
          api.edit(this.editForm, (res) => {
            if (!res) return;
            this.isEdit = false;
            this.apiGet();
            utils.success('网站云修改成功');
          });
        }
      });
    },

    btnShellClick(host: IHost) {
      api.shell(host.id, (res) => {
        if (!res) return;
        this.isShell = true;
        this.shellResult = res;
      });
    },

    btnCertClick(host: IHost) {
      utils.alertDelete(
        {
          title: '更新域名HTTPS证书',
          text: `此操作将更新 ${this.getUniqueName(host)} 的域名HTTPS证书，确定吗？`,
          button: '确认更新',
        },
        () => {
          api.updateDomainsCert(host.id, (res) => {
            if (!res) return;
            utils.success('域名HTTPS证书更新成功！');
            this.apiGet();
          });
        },
      );
    },

    btnCredentialsClick(host: IHost) {
      this.host = { ...host };
      api.credentialsGet(host.userName, host.id, (res) => {
        if (!res) return;
        this.credentialsForm = {
          hostId: this.host.id,
          securityKey: res.securityKey,
          ftpPassword: res.ftpPassword,
          databaseUser: res.databaseUser,
          databasePassword: res.databasePassword,
          databaseName: res.databaseName,
        };
        this.isCredentials = true;
      });
    },

    btnCredentialsSubmitClick() {
      api.credentialsSubmit(this.credentialsForm, (res) => {
        if (!res) return;
        utils.success('网站云凭证保存成功');
        this.isCredentials = false;
      });
    },

    btnConnectionClick(host: IHost) {
      api.credentialsGet(host.userName, host.id, (res) => {
        this.connections = utils.getHostConnections(
          host,
          res.isHongKong,
          res.securityKey,
          res.ftpPassword,
          res.databaseHost,
          res.databaseUser,
          res.databasePassword,
          res.databaseName,
        );
        this.isConnections = true;
      });
    },

    apiAgentInstall(success: () => void) {
      agentInstall.install(
        this.host.hostName,
        this.host.nodeName,
        this.host.isHongKong,
        {
          securityKey: this.resetPasswordForm.securityKey,
          userName: this.resetPasswordForm.userName,
          password: this.resetPasswordForm.password,
        },
        (res) => {
          if (res.value) {
            success();
          }
        },
      );
    },

    apiResetPasswordSubmit() {
      api.resetPasswordSubmit(
        {
          hostId: this.host.id,
          userName: this.resetPasswordForm.userName,
          password: this.resetPasswordForm.password,
        },
        (res) => {
          if (res.value) {
            utils.success('恭喜，重置密码成功');
            this.isResetPassword = false;
            this.apiGet();
            utils.loading(false);
          }
        },
      );
    },

    btnResetPasswordSubmitClick() {
      this.apiAgentInstall(() => {
        this.apiResetPasswordSubmit();
      });
    },

    validatePass(rule: string, value: string, callback: (error?: Error) => void) {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.resetPasswordForm.password) {
        callback(new Error('两次输入密码不一致!'));
      } else {
        callback();
      }
    },
  },
  mounted() {
    this.apiGet();
  },
});
